<script>
import * as m from '@app/paraglide/messages.js';
import { onMount, onDestroy } from 'svelte';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    editorState
} from '@app/store/editorStore.svelte.js';
import {
    playerState
} from '@app/store/playerStore.svelte.js';
import {
    fade
} from 'svelte/transition';
import {
    canGenerateFilmstrip,
    preRenderFilmstripCache,
    FILMSTRIP_FRAME_WIDTH
} from '@app/lib/filmstrip.js';

let progress = $state(0);
let statusMsg = $state(m.pre_render_frame_cache_starting_status());
let currentJob = null;
let userCancelled = false;
let finished = false;

function collectStartTimes() {
    const starts = [];
    for (const group of eventGroupState) {
        if (!group?.events?.length) continue;
        for (const event of group.events) {
            if (Number.isFinite(Number(event?.start))) {
                starts.push(Number(event.start));
            }
        }
    }
    return starts;
}

function updateProgress({ completed, total }) {
    if (total <= 0) {
        progress = 100;
        return;
    }
    progress = Math.min(100, Number(((completed / total) * 100).toFixed(1)));
    statusMsg = m.pre_render_frame_cache_progress_status({
        current: String(completed),
        total: String(total)
    });
}

async function cancelJob() {
    if (finished || userCancelled) return;
    userCancelled = true;
    if (currentJob) {
        currentJob.cancel();
        currentJob = null;
    }
    toast.push(m.pre_render_frame_cache_cancelled_toast(), { classes: ['toast-info'] });
    hideModal();
}

async function runPreRender() {
    if (!canGenerateFilmstrip(projectState)) {
        toast.push(m.pre_render_frame_cache_unavailable_toast(), { classes: ['toast-warning'] });
        hideModal();
        return;
    }

    const startTimes = collectStartTimes();
    if (startTimes.length === 0) {
        toast.push(m.pre_render_frame_cache_no_events_toast(), { classes: ['toast-warning'] });
        hideModal();
        return;
    }

    toast.push(m.pre_render_frame_cache_starting_toast(), { classes: ['toast-info'] });

    const frameCount = Math.min(30, Math.max(1, Math.round(Number(editorState.filmstripFrameCount) || 12)));
    const duration = Number.isFinite(playerState.duration) && playerState.duration > 0
        ? playerState.duration
        : (projectState?.media?.duration ?? projectState?.duration);

    const job = preRenderFilmstripCache({
        projectState,
        startTimes,
        frameCount,
        width: FILMSTRIP_FRAME_WIDTH,
        duration,
        onProgress: updateProgress
    });
    currentJob = job;

    try {
        const result = await job.promise;
        if (userCancelled || result.cancelled) {
            return;
        }
        finished = true;
        progress = 100;
        toast.push(m.pre_render_frame_cache_complete_toast({
            cached: String(result.cached),
            generated: String(result.generated)
        }), { classes: ['toast-success'] });
        hideModal();
    } catch (err) {
        if (userCancelled) return;
        finished = true;
        const message = err && err.message ? err.message : String(err);
        toast.push(m.pre_render_frame_cache_failed_toast({ error: message }), { classes: ['toast-warning'] });
        statusMsg = m.pre_render_frame_cache_failed_status({ error: message });
        progress = 100;
        hideModal();
    } finally {
        currentJob = null;
    }
}

onMount(() => {
    runPreRender();
});

onDestroy(() => {
    if (!finished && currentJob) {
        userCancelled = true;
        currentJob.cancel();
        currentJob = null;
    }
});
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'preRenderFrameCache' ? 'show d-block' : ''}"
    role="dialog"
    aria-labelledby="preRenderFrameCacheTitle"
    tabindex="-1"
    id="PreRenderFrameCacheModal"
>
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="preRenderFrameCacheTitle">{m.pre_render_frame_cache_title()}</h4>
            </div>
            <div class="modal-body">
                <p>
                    <i class="bi bi-film" role="img" aria-hidden="true"></i>
                    {m.pre_render_frame_cache_working_text()}
                </p>
                <div
                    class="progress"
                    role="progressbar"
                    aria-label={m.pre_render_frame_cache_progress_aria_label()}
                    aria-valuenow={progress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                >
                    <div
                        class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                        style="width: {progress}%;"
                    >
                        {progress}%
                    </div>
                </div>
                <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-danger" onclick={cancelJob}>
                    {m.pre_render_frame_cache_cancel_button()}
                </button>
            </div>
        </div>
    </div>
</div>
